import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Banner, Button, ButtonLink, Card, Dialog, Field, formatMoney, isZero, Money, PageHeader, Pill, Tabs, type LinkComponent,
} from '@elysian/ui';
import type { DocumentListQuery, DocumentSort, DocumentSummary, LedgerDocument } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { invoiceState, iso, longDate, shortDate, type InvoiceFilter } from '../invoices/shared';
import { Pager, ShowSize, SortTh, useRemembered, useSettled, type SortState } from '../shared/ListControls';
import { Attachments } from '../shared/Attachments';
import { ExportMenu } from '../shared/ExportMenu';
import { billColumns } from '../shared/exportColumns';
import { RecordReceipt, receiptToast } from '../contacts/RecordReceipt';

const RouterLink = Link as unknown as LinkComponent;

type BillFilter = InvoiceFilter | 'credits';

export function Bills() {
  const { source, today, context } = useSession();
  const base = context?.organisation.base_currency ?? 'EUR';
  const navigate = useNavigate();
  const now = iso(today());
  // Filtered, sorted, counted and paged on the server, over every bill (#49, #50); where you were is kept for the session.
  const [filter, setFilter] = useRemembered<BillFilter>('elysian.bills.tab', 'unpaid', 'session');
  const [q, setQ] = useRemembered('elysian.bills.q', '', 'session');
  const [page, setPage] = useRemembered('elysian.bills.page', 0, 'session');
  const [size, setSize] = useRemembered('elysian.bills.size', 50);
  const [sortBy, setSortBy] = useRemembered<SortState<DocumentSort>>('elysian.bills.sort', { sort: 'issued', dir: 'desc' });
  const settledQ = useSettled(q);
  const query: DocumentListQuery = { side: 'purchases', tab: filter === 'credits' ? 'credit' : filter,
    ...(settledQ.trim() ? { q: settledQ.trim() } : {}), sort: sortBy.sort, dir: sortBy.dir };
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setPage(0);
  }, [filter, settledQ, sortBy.sort, sortBy.dir, size, setPage]);
  const data = useAsync(() => source.documentList({ ...query, ...(size ? { limit: size, offset: page * size } : {}) }),
    [source, JSON.stringify(query), size, page], { keep: true });
  const list = data.state === 'ready' ? data.data : null;
  const rows = list?.items ?? [];
  const count = (f: keyof NonNullable<typeof list>['counts']) => list?.counts[f] ?? 0;

  return (
    <>
      <PageHeader title="Bills" subtitle={list ? `You owe ${formatMoney(list.totals.unpaid, base)} on ${count('unpaid')} ${count('unpaid') === 1 ? 'bill' : 'bills'}` : 'Supplier bills'}
        actions={<>
          <ButtonLink as={RouterLink} to="/bills/new-credit" variant="ghost">Supplier credit note</ButtonLink>
          <ButtonLink as={RouterLink} to="/bills/new">New bill</ButtonLink>
          <ButtonLink as={RouterLink} to="/inbox" variant="primary">Bill inbox</ButtonLink>
        </>} />
      {data.state === 'error' && <Banner>Couldn’t load bills: {data.error.message}</Banner>}
      <Card>
        <div className="el-spread" style={{ padding: '14px 16px 0', gap: 12, flexWrap: 'wrap' }}>
          <label className="el-search" style={{ flex: '0 1 320px', minWidth: 'min(260px, 100%)' }}>
            <input id="bill-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Supplier or their invoice number" aria-label="Search bills" />
          </label>
          <span className="el-hint" style={{ flex: '1 1 200px' }}>Bills with a file go through the bill inbox; type in the rest with New bill</span>
          <div className="el-row" style={{ gap: 12, alignItems: 'center' }}>
            <ShowSize value={size} onChange={setSize} />
            <ExportMenu name="Bills" columns={billColumns} rows={rows} disabled={!list}
              all={list ? { count: list.count, load: async () => (await source.documentList(query)).items } : undefined} />
          </div>
        </div>
        <Tabs<BillFilter> label="Show bills" value={filter} onChange={setFilter} tabs={[
          { key: 'unpaid', label: 'Unpaid', count: count('unpaid') },
          { key: 'overdue', label: 'Overdue', count: count('overdue') },
          { key: 'paid', label: 'Paid', count: count('paid') },
          { key: 'all', label: 'All', count: count('all') },
          ...(count('draft') || filter === 'draft' ? [{ key: 'draft' as const, label: 'Drafts', count: count('draft') }] : []),
          ...(count('credit') || filter === 'credits' ? [{ key: 'credits' as const, label: 'Credit notes', count: count('credit') }] : []),
        ]} />
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Bills">
            <thead><tr>
              <SortTh label="Supplier" col="contact" state={sortBy} onSort={setSortBy} />
              <SortTh label="Their number" col="number" state={sortBy} onSort={setSortBy} />
              <SortTh label="Dated" col="issued" state={sortBy} onSort={setSortBy} firstDir="desc" />
              <SortTh label="Due" col="due" state={sortBy} onSort={setSortBy} firstDir="desc" />
              <th scope="col">Status</th>
              <SortTh label="Total" col="total" state={sortBy} onSort={setSortBy} numeric />
              <SortTh label="To pay" col="outstanding" state={sortBy} onSort={setSortBy} numeric />
            </tr></thead>
            <tbody>
              {data.state === 'loading' && <tr><td colSpan={7}><div className="el-skeleton" style={{ height: 18 }} /></td></tr>}
              {rows.map((d) => {
                const credit = d.type === 'purchase_credit';
                const s = credit && d.status !== 'draft'
                  ? { tone: isZero(d.outstanding) ? 'ok' as const : 'info' as const, label: isZero(d.outstanding) ? 'Credit used' : 'Credit to use' }
                  : invoiceState(d, now);
                return (
                  <tr key={d.id} data-href={`/bills/${d.id}`} onClick={(e) => (e.target as HTMLElement).closest('a') || navigate(`/bills/${d.id}`)}>
                    <td><Link className="el-row-link" style={{ whiteSpace: 'normal' }} to={`/bills/${d.id}`}>{d.contact_name}</Link></td>
                    <td>{d.supplier_ref ?? '—'}{credit && <div className="el-hint">Credit note</div>}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{shortDate(d.issue_date)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{d.due_date && !credit ? shortDate(d.due_date) : '—'}</td>
                    <td><Pill tone={s.tone}>{s.label}</Pill></td>
                    <td className="el-num"><Money value={d.gross_total} currency={d.currency} />
                      {d.currency !== base && <div className="el-hint"><Money value={d.base_gross_total} currency={base} /></div>}</td>
                    <td className="el-num">{isZero(d.outstanding) || d.status === 'void' ? '—' : <>
                      <Money value={d.outstanding} currency={d.currency} />
                      {d.currency !== base && <div className="el-hint"><Money value={d.base_outstanding} currency={base} /></div>}</>}</td>
                  </tr>
                );
              })}
              {data.state === 'ready' && rows.length === 0 && <tr><td colSpan={7} className="el-muted">No bills here.</td></tr>}
            </tbody>
          </table>
        </div>
        {list && <Pager count={list.count} page={page} size={size} shown={rows.length} onPage={setPage} />}
      </Card>
    </>
  );
}

export function BillPage() {
  const { id = '' } = useParams();
  const { source, today, can, context } = useSession();
  const base = context?.organisation.base_currency ?? 'EUR';
  const toast = useToast((s) => s.show);
  const now = iso(today());
  const [version, setVersion] = useState(0);
  const data = useAsync(() => source.document(id), [source, id, version], { keep: true });
  const [voiding, setVoiding] = useState(false);
  const [paying, setPaying] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (data.state === 'loading') return <PageHeader title="Bill" subtitle="Loading…" />;
  if (data.state === 'error') return <><PageHeader title="Bill" /><Banner action={<Link to="/bills">All bills</Link>}>Couldn’t open this bill: {data.error.message}</Banner></>;
  const d = data.data;
  const credit = d.type === 'purchase_credit';
  const draft = d.status === 'draft';
  const s = invoiceState(d as DocumentSummary, now);
  const open = d.status === 'posted' || d.status === 'part_paid';
  const noun = credit ? 'credit note' : 'bill';

  async function doVoid() {
    setBusy(true);
    setError(null);
    try {
      await source.voidDocument(d.id, now, reason.trim());
      toast(`${credit ? 'Credit note' : 'Bill'} ${d.supplier_ref ?? d.doc_no} voided`);
      setVoiding(false);
      setVersion((v) => v + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div style={{ marginBottom: 6 }}><Link to="/bills" className="el-hint">← All bills</Link></div>
      <PageHeader title={`${d.contact_name} ${d.supplier_ref ?? ''}`.trim()}
        subtitle={<>{credit ? 'Supplier credit note · ' : ''}Our reference {d.doc_no ?? 'draft'} · dated {longDate(d.issue_date)}{d.due_date && !credit && <> · due {longDate(d.due_date)}</>}</>}
        actions={draft
          ? <ButtonLink as={RouterLink} to={`/bills/${d.id}/edit`} variant="primary">Edit and approve</ButtonLink>
          : open && can('documents.write') ? <>
            {!credit && d.currency === base && <Button variant="primary" onClick={() => setPaying(true)}>Record a payment</Button>}
            {(d.allocations?.length ?? 0) === 0 && <Button variant="danger" onClick={() => { setError(null); setReason(''); setVoiding(true); }}>Void</Button>}
          </> : undefined} />
      <div className="el-row" style={{ marginBottom: 16 }}>
        <Pill tone={s.tone}>{s.label}</Pill>
        <Link className="el-hint" to={`/suppliers/${d.contact_id}`}>Supplier’s account</Link>
      </div>
      <div className="el-invoice-layout">
        <Card as="section" padding="18px 0 12px" aria-labelledby="bill-lines">
          <div className="el-card-head"><h2 id="bill-lines" className="el-h2">Lines</h2></div>
          <div className="el-table-wrap">
            <table className="el-table" aria-label="Bill lines">
              <thead><tr><th scope="col">Description</th><th scope="col">Account</th><th scope="col" className="el-num">Net</th><th scope="col" className="el-num">VAT</th></tr></thead>
              <tbody>
                {d.lines.map((l) => (
                  <tr key={l.line_no}><td>{l.description}</td><td>{l.account_code}</td><td className="el-num"><Money value={l.net_amount} currency={d.currency} /></td><td className="el-num"><Money value={l.tax_amount ?? '0'} currency={d.currency} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <dl className="el-totals" style={{ margin: '14px 20px 0 auto' }}>
            <div><dt>Net</dt><dd className="el-num"><Money value={d.net_total} currency={d.currency} /></dd></div>
            <div className="el-muted"><dt>VAT</dt><dd className="el-num"><Money value={d.tax_total} currency={d.currency} /></dd></div>
            <div className="el-totals-grand"><dt>Total</dt><dd className="el-num"><Money value={d.gross_total} currency={d.currency} /></dd></div>
            {open && <div><dt>{credit ? 'Still to use' : 'Still to pay'}</dt><dd className="el-num"><Money value={d.outstanding} currency={d.currency} /></dd></div>}
            {d.currency !== base && (
              <div className="el-muted" aria-label={`In ${base}`}>
                <dt>In {base} at {Number(d.fx_rate ?? '1').toString()}</dt>
                <dd className="el-num"><Money value={d.base_gross_total} currency={base} /></dd>
              </div>
            )}
          </dl>
        </Card>
        <div className="el-stack">
        <Card as="section" padding="18px 20px" aria-labelledby="bill-history">
          <h2 id="bill-history" className="el-h2" style={{ marginBottom: 10 }}>History</h2>
          <ol className="el-timeline">
            <li><span>{draft ? 'Draft: not in the books until approved' : 'Approved and posted'}</span><span className="el-hint">{shortDate(d.issue_date)}</span></li>
            {(d.allocations ?? []).map((a, i) => (
              <li key={i}><span>{credit ? `Used against ${a.doc_no}` : a.doc_no?.startsWith('SCN-') ? `Credit ${a.doc_no}` : a.doc_no?.startsWith('Paid from') ? a.doc_no.replace('Paid from', 'Paid on the spot from') : 'Paid'}{' '}
                <Money value={a.amount} currency={d.currency} /></span><span className="el-hint">{shortDate(a.date)}</span></li>
            ))}
            {d.status === 'void' && <li><span>Voided. A reversing journal was posted.</span></li>}
          </ol>
          {open && !credit && <p className="el-hint">Record a payment here, or match it on the <Link to="/bank">bank screen</Link>. A credit note from {d.contact_name} can be <Link to={`/bills/new-credit?bill=${d.id}`}>entered against it</Link>.</p>}
        </Card>
        {credit && open && can('documents.write') && <UseCredit credit={d} onUsed={() => setVersion((v) => v + 1)} />}
        <Card as="section" padding="18px 20px" aria-labelledby="bill-files">
          <h2 id="bill-files" className="el-h2" style={{ marginBottom: 10 }}>Files</h2>
          <Attachments files={d.attachments ?? []} target={{ document_id: d.id }} canAdd={can('documents.write') && d.status !== 'void'}
            label={`bill ${d.supplier_ref ?? d.doc_no ?? ''}`.trim()} onChanged={() => setVersion((v) => v + 1)} />
        </Card>
        </div>
      </div>
      {paying && (
        <RecordReceipt role="supplier" contactId={d.contact_id} contactName={d.contact_name} invoiceId={d.id} onClose={() => setPaying(false)}
          onDone={(r) => { setPaying(false); toast(receiptToast(r)); setVersion((v) => v + 1); }} />
      )}
      {voiding && (
        <Dialog title={`Void this ${noun}?`} onClose={() => setVoiding(false)}
          actions={<><Button variant="ghost" onClick={() => setVoiding(false)}>Keep it</Button><Button variant="danger" disabled={busy || reason.trim().length < 3} onClick={() => void doVoid()}>Void {noun}</Button></>}>
          <p style={{ margin: 0 }}>A reversing journal is posted today; the {noun} stays on file, marked void.{credit ? '' : ' If the supplier sent a credit note instead, record that.'}</p>
          <Field id="bill-void-reason" label="Reason"><input id="bill-void-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Entered twice" /></Field>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
    </>
  );
}

/** A supplier credit note set against the same supplier's unpaid bills. */
function UseCredit({ credit, onUsed }: { credit: LedgerDocument; onUsed: () => void }) {
  const { source } = useSession();
  const toast = useToast((t) => t.show);
  const bills = useAsync(() => source.documents({ type: 'purchase_bill', contactId: credit.contact_id }), [source, credit.contact_id]);
  const open = (bills.state === 'ready' ? bills.data : [])
    .filter((b) => (b.status === 'posted' || b.status === 'part_paid') && b.currency === credit.currency && !isZero(b.outstanding));
  const [bill, setBill] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chosen = open.find((b) => b.id === bill);
  const suggested = chosen ? (Number(chosen.outstanding) < Number(credit.outstanding) ? chosen.outstanding : credit.outstanding) : '';

  async function use() {
    setBusy(true);
    setError(null);
    try {
      const amt = (amount.trim() || Number(suggested).toFixed(2));
      await source.allocateCredit(credit.id, [{ document_id: bill, amount: amt }]);
      toast(`${formatMoney(amt, credit.currency)} used against ${chosen?.supplier_ref ?? chosen?.doc_no}`);
      setBill('');
      setAmount('');
      onUsed();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card as="section" padding="18px 20px" aria-labelledby="credit-use">
      <h2 id="credit-use" className="el-h2" style={{ marginBottom: 6 }}>Use against a bill</h2>
      {bills.state === 'ready' && open.length === 0
        ? <p className="el-hint" style={{ margin: 0 }}>{credit.contact_name} has no unpaid {credit.currency} bills. The credit stays on their account until one comes in, or a refund is matched on the bank screen.</p>
        : (
          <div className="el-stack" style={{ gap: 10 }}>
            <Field id="credit-bill" label="Bill">
              <select id="credit-bill" className="el-input" value={bill} onChange={(e) => { setBill(e.target.value); setAmount(''); }}>
                <option value="">Choose a bill…</option>
                {open.map((b) => <option key={b.id} value={b.id}>{b.supplier_ref ?? b.doc_no} · {shortDate(b.issue_date)} · {formatMoney(b.outstanding, b.currency)} to pay</option>)}
              </select>
            </Field>
            {chosen && (
              <Field id="credit-amount" label="Amount" hint={`Up to ${formatMoney(suggested, credit.currency)}`}>
                <input id="credit-amount" className="el-input el-num" inputMode="decimal" placeholder={Number(suggested).toFixed(2)} value={amount} onChange={(e) => setAmount(e.target.value)} />
              </Field>
            )}
            <div><Button variant="primary" disabled={busy || !chosen} onClick={() => void use()}>Use the credit</Button></div>
          </div>
        )}
      {error && <div className="el-error-box" role="alert" style={{ marginTop: 10 }}>{error}</div>}
    </Card>
  );
}
